import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { bandTone, sortBands } from "./taskHubUtils";

// The audit view of one scored task: the latest grade per location, every
// audit's score over time, which sections and items fail most across the
// company, and each audit's fails with the auditor's notes. Pure CSS like
// the rest of the Reports pages.

const pctLabel = (v) => (v == null ? "–" : `${Math.round(v * 100)}%`);
const scoreText = (occ) => (occ.score_percent == null ? "–" : `${occ.score_percent}%`);

// Each audit is coloured against the bands it was graded under (its own
// stamped snapshot), so an audit from before a band edit keeps its meaning.
function GradeChip({ occ, bands, onClick, title }) {
  const tone = bandTone(occ.score_grade, occ.bands || bands);
  const inner = (
    <span
      className={`inline-flex flex-col items-center justify-center rounded-lg border px-2 py-1 min-w-[56px] leading-tight ${tone.chip} ${
        onClick ? "cursor-pointer hover:ring-2 hover:ring-violet-300" : ""
      }`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <span className="text-sm font-extrabold">{occ.score_grade || "–"}</span>
      <span className="text-[10px] font-semibold tabular-nums">{scoreText(occ)}</span>
    </span>
  );
  return title ? <Tooltip title={title}>{inner}</Tooltip> : inner;
}

function Tiles({ audit, bands }) {
  const lowest = bands.length ? bands[bands.length - 1].label : "lowest band";
  const tiles = [
    {
      label: "Average score",
      value: audit.avg_percent == null ? "–" : `${audit.avg_percent}%`,
      sub: audit.avg_percent == null ? "no audits yet" : `across ${audit.audits_completed} audit${audit.audits_completed === 1 ? "" : "s"}`,
    },
    { label: "Audits completed", value: audit.audits_completed, sub: "in this range" },
    {
      // Counted from each audit's own stamped bands; the current lowest
      // band's name is only the caption.
      label: "Locations in the lowest band",
      value: audit.stores_in_lowest_band,
      sub: `by their latest audit · currently "${lowest}"`,
      dot: bandTone(lowest, bands).hex,
    },

    {
      label: "Locations audited",
      value: (audit.latest_by_store || []).length,
      sub: "at least once",
    },
  ];
  return (
    <div className="grid grid-cols-4 sm:grid-cols-2 gap-3">
      {tiles.map((t) => (
        <div key={t.label} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            {t.dot && <span className="w-2 h-2 rounded-full" style={{ background: t.dot }} />}
            {t.label}
          </div>
          <div className="text-2xl font-bold text-slate-900 tabular-nums mt-1.5">{t.value}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

// Locations down, audit dates across; each cell is that audit's grade.
function StoreGrid({ audit, bands, homeStore, onOpenAudit }) {
  const occurrences = audit.occurrences || [];
  if (!occurrences.length) return null;
  const dates = [...new Set(occurrences.map((o) => o.date))].sort();
  const shown = dates.slice(-10);
  const stores = [...new Map(occurrences.map((o) => [o.store_id, o.store_name])).entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  // Newest first in the payload; a location audited twice on one date keeps
  // the latest submission.
  const byKey = {};
  occurrences.forEach((o) => {
    const key = `${o.store_id}:${o.date}`;
    if (!byKey[key]) byKey[key] = o;
  });

  const gridCols = `minmax(180px,1.5fr) repeat(${shown.length}, 76px)`;
  const dateLabel = (d) =>
    new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
      <div className="px-4 py-3 border-b border-slate-100 flex items-baseline gap-2">
        <span className="text-sm font-bold text-slate-900">Scores by location</span>
        <span className="text-[11px] text-slate-400">
          {dates.length > shown.length ? `last ${shown.length} audit dates` : "each audit's grade"}
          {onOpenAudit ? " · click a grade at this location to open it" : ""}
        </span>
      </div>
      <div style={{ minWidth: 180 + shown.length * 76 + 32 }}>
        <div
          className="grid gap-2 items-end px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400"
          style={{ gridTemplateColumns: gridCols }}
        >
          <span>Location</span>
          {shown.map((d) => (
            <span key={d} className="text-center">{dateLabel(d)}</span>
          ))}
        </div>
        {stores.map((s) => (
          <div
            key={s.id}
            className="grid gap-2 items-center px-4 py-2 border-b border-slate-100 last:border-b-0"
            style={{ gridTemplateColumns: gridCols }}
          >
            <span className="text-sm font-medium text-slate-800 truncate">{s.name}</span>
            {shown.map((d) => {
              const occ = byKey[`${s.id}:${d}`];
              if (!occ) return <span key={d} className="text-center text-slate-200">·</span>;
              const canOpen = onOpenAudit && occ.store_id === homeStore;
              return (
                <span key={d} className="flex justify-center">
                  <GradeChip
                    occ={occ}
                    bands={bands}
                    title={`${occ.score_passed} / ${occ.score_total} · ${occ.fail_count} failed${occ.validated ? "" : " · awaiting validation"}${occ.submitted_by ? ` · ${occ.submitted_by}` : ""}`}
                    onClick={canOpen ? () => onOpenAudit(occ) : undefined}
                  />
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionRates({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 text-sm font-bold text-slate-900 border-b border-slate-100">
        Pass rate by section
      </div>
      {rows.map((r) => {
        const pct = r.pass_rate == null ? 0 : Math.round(r.pass_rate * 100);
        const bar = pct >= 90 ? "bg-emerald-600" : pct >= 80 ? "bg-amber-600" : "bg-rose-600";
        return (
          <div key={r.section} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0">
            <span className="flex-1 text-sm font-medium text-slate-800 truncate">{r.section}</span>
            <span className="w-28 h-1.5 rounded-full bg-slate-100 border border-slate-200 overflow-hidden">
              <span className={`block h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
            </span>
            <span className="text-xs text-slate-500 tabular-nums w-16 text-right">
              {r.fails} of {r.answered}
            </span>
            <span className="text-xs font-semibold text-slate-700 tabular-nums w-12 text-right">
              {pctLabel(r.pass_rate)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StepFails({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-baseline gap-2">
        <span className="text-sm font-bold text-slate-900">Most failed items</span>
        <span className="text-[11px] text-slate-400">across every audit in range</span>
      </div>
      {rows.slice(0, 15).map((r) => (
        <div key={r.plan_subtask_uuid} className="flex items-start gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0">
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium text-slate-800 truncate">{r.name}</span>
            <span className="block text-[11px] text-slate-400 truncate">
              {[r.section, (r.worst_stores || []).map((s) => `${s.store_name} ×${s.fails}`).join(", ")]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
          <span className="text-xs text-rose-600 font-semibold tabular-nums whitespace-nowrap">
            {r.fails} of {r.answered}
          </span>
          <span className="text-xs text-slate-500 tabular-nums w-10 text-right">{pctLabel(r.fail_rate)}</span>
        </div>
      ))}
    </div>
  );
}

function AuditList({ occurrences, bands, homeStore, onOpenAudit }) {
  const [openUuid, setOpenUuid] = React.useState(null);
  if (!occurrences?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 text-sm font-bold text-slate-900 border-b border-slate-100">Audits</div>
      {occurrences.map((o) => {
        const expanded = openUuid === o.plan_task_assignment_uuid;
        const canOpen = onOpenAudit && o.store_id === homeStore;
        return (
          <div key={o.plan_task_assignment_uuid} className="border-b border-slate-100 last:border-b-0">
            <button
              type="button"
              onClick={() => setOpenUuid(expanded ? null : o.plan_task_assignment_uuid)}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50"
            >
              <GradeChip occ={o} bands={bands} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-slate-800 truncate">{o.store_name}</span>
                <span className="block text-[11px] text-slate-400">
                  {[o.date, o.submitted_by, o.validated ? null : "awaiting validation"].filter(Boolean).join(" · ")}
                </span>
              </span>
              <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
                {o.fail_count} failed
              </span>
            </button>
            {expanded && (
              <div className="px-4 pb-3 pl-[88px]">
                {o.fails.length === 0 ? (
                  <p className="text-xs text-slate-400">Everything passed.</p>
                ) : (
                  o.fails.map((f) => (
                    <div key={f.plan_subtask_assignment_uuid} className="py-1.5 border-t border-slate-100 first:border-t-0">
                      <span className="block text-sm text-slate-800">{f.name}</span>
                      <span className="block text-[11px] text-slate-400">
                        {[f.section, f.note ? `“${f.note}”` : null].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                  ))
                )}
                {canOpen && (
                  <button
                    type="button"
                    onClick={() => onOpenAudit(o)}
                    className="mt-2 text-xs font-semibold text-violet-700 hover:text-violet-800"
                  >
                    Open this audit
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AuditReport({ audit, homeStore, onOpenAudit }) {
  if (!audit) return null;
  const bands = sortBands(audit.grade_bands);
  return (
    <div className="flex flex-col gap-4">
      <Tiles audit={audit} bands={bands} />
      <StoreGrid audit={audit} bands={bands} homeStore={homeStore} onOpenAudit={onOpenAudit} />
      <div className="grid grid-cols-2 sm:grid-cols-1 gap-4 items-start">
        <StepFails rows={audit.step_fails} />
        <SectionRates rows={audit.section_rates} />
      </div>
      <AuditList occurrences={audit.occurrences} bands={bands} homeStore={homeStore} onOpenAudit={onOpenAudit} />
    </div>
  );
}
